import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { SButton } from '@chargebee/sting-react';
import {
  PLAN_REMINDER_DAYS,
  useDaysToExpiration,
  usePlanPurchaseIntent,
  type Expiration,
} from '@bbkAdminRedux/rtkq/checkout.slice';
import { useRoles } from '@bbkAdminUtils/roles';
import { stingColors } from '@bbkShared/sting-colors';
import { Constants } from '@bbkShared/utils/constants/ui-constants';

const Banner = styled.div`
  display: flex;
  flex-direction: row;
  flex-basis: 80px;
  flex-shrink: 0;
  justify-content: space-between;
  width: 100%;
  color: white;
  background: ${stingColors.primary['400']};
  padding: 20px;
`;

type EndOfPlanBannerProps = {
  suppressExpiredTrial?: boolean;
};

export const EndOfPlanBanner: React.FC<EndOfPlanBannerProps> = ({
  suppressExpiredTrial = false,
}) => {
  const { hasBillingRole } = useRoles();
  const expiration: Expiration | undefined = useDaysToExpiration();
  const [message, setMessage] = useState<string>('');
  const [subMessage, setSubMessage] = useState<string>('');
  const handlePlanPurchaseIntent = usePlanPurchaseIntent();
  useEffect(() => {
    if (!expiration) {
      setMessage('');
      setSubMessage('');
      return;
    }
    const { isTrial, endDate } = expiration;
    const daysToExpiry = endDate.diff(moment.utc(), 'days');
    if (daysToExpiry < 0) {
      setMessage(
        `Your ${
          isTrial ? '30 day free trial' : 'plan'
        } ended on ${endDate.format('MMM D, YYYY')} UTC`
      );
      setSubMessage(
        hasBillingRole
          ? 'To publish your pages and deploy traffic you will need to upgrade.'
          : ''
      );
    } else if (daysToExpiry <= PLAN_REMINDER_DAYS) {
      setMessage(
        `Your ${isTrial ? 'trial' : 'plan'} is due to end on ${endDate.format(
          'MMM D, YYYY'
        )} UTC`
      );
      setSubMessage(
        hasBillingRole
          ? 'Click upgrade to ensure your traffic keeps routing to your cancel page(s).'
          : ''
      );
    }
  }, [expiration, hasBillingRole]);

  if (suppressExpiredTrial) return null;

  return (
    <>
      {message && (
        <Banner>
          <div>
            <div>
              <strong>{message}</strong>
            </div>
            <div>
              <p css={{ 'font-size': '0.75em' }}>{subMessage}</p>
            </div>
          </div>
          <div>
            {hasBillingRole && (
              <a
                href={Constants.PurchaseLink}
                rel="noreferrer noopener"
                target="_blank"
              >
                <SButton
                  variant="primary"
                  onClick={() => {
                    handlePlanPurchaseIntent();
                  }}
                  css={{ 'border-radius': '10em' }}
                >
                  Purchase
                </SButton>
              </a>
            )}
          </div>
        </Banner>
      )}
    </>
  );
};
